import {
  APIRetry,
  Choice,
  Constants,
  ContentType,
  DiagnosticsActionResult,
  DiagnosticsActionStatus,
  DynamicChoiceValue,
  DynamicVariableValue,
  ExternalApi,
  GraphNode,
  LogEventName,
  LogLevel,
  LogMessageCodes,
  ParamOrVar,
  RegExpForParamOrVar,
  SAFActionMap,
  Target,
  TextField,
  JsonDocument,
  ContentIdResource,
  NodeType
} from '../../models';
import { AlchemyResource, TextField as IntentGraphTextField } from '../../models/intentGraph.models';
import {
  DiagnosticsExecutionStatus,
  DiagnosticsInitResponse,
  DiagnosticsProgressRequest,
  DiagnosticsProgressResponse,
  DiagnosticsStartRequest,
  ErrorCodes,
  FormV2,
  ISAFActionExecutionResponse,
  SAFActionProvider,
  SAFActionSchemaResponse,
  SAFContextVariable
} from '../../models/safDiagnostic.models';
import { YodaState } from '../../rootReducer';
import { deserializeToImmutableMap } from '../../serialize';
import { createImmutableList, createImmutableMap, IImmutableList, IImmutableMap, MDispatch } from '../../state/redux';
import {
  DiagnosticsActionHandlerType,
  GetDiagnosticsResultType,
  IsUserSignedInHandlerType,
  LogHandlerType,
  NodeRenderedHandlerType,
  ExecuteSAFActionType,
  GetSAFActionSchemaType,
  SafDiagnosticsRendererType,
  StartDiagnosticsType,
  ContentRequestHandlerType,
  PartnerSolutionRendererType
} from '../../types';
import {
  generateAdhocs,
  executeLogHandler,
  isNullOrUndefined,
  setSignInNode,
  getNodeLogProperties,
  readValue,
  findAndReplaceVariables,
  getJson,
  checkNodePreCondition,
  evalTargetConditions,
  updateAdhocVariableValues
} from '../../utils';

import {
  abandonDiagnosticsRunAction,
  cancelEditNodeChoiceSelectionAction,
  diagnosticsCompletedAction,
  diagnosticsStartedAction,
  editNodeChoiceSelectionAction,
  fetchingSafDiagnosticsAction,
  fetchDialogCompleteAction,
  incrementDiagnosticsRunProgressAction,
  loadDiagnosticResourceAction,
  pushTargetNodeFromInputNodeAction,
  pushTargetNodeOnRenderedNodesAction,
  resetApiRetryCountAction,
  resetErrorAction,
  safDiagnosticRenderedAction,
  safDiagnosticsCompletedAction,
  setDiagnosticsRunProgress,
  setDiagnosticsStatusAction,
  setErrorAction,
  startDiagnosticsRunAction,
  startDiagnosticsTimerAction,
  stopDiagnosticsTimerAction,
  updateApiRetryCountAction,
  updateDiagnosticsFormComponentAction,
  updateGlobalVarsAction,
  updateInputTextAction,
  fetchDialogStartedAction,
  setContextVarsAction,
  fetchAlchemyResourceAction,
  setAlchemyResourceAction,
  updateInputFormFieldsAction
} from './actions';
import {
  apiRetriesSelector,
  diagnosticsResponseSelector,
  enableUsingCallBacksForSAFApisSelector,
  getJsonPayload,
  getResource,
  globalVarsSelector,
  jsonPayloadsSelector,
  renderedNodesSelector,
  resourcesSelector,
  sessionIdSelector
} from './selectors.redux';

export const dispatchPushTargetNode = (
  uId: string,
  selectedChoice: IImmutableMap<Choice>,
  isDynamicChoice: boolean,
  choiceIndex: number,
  node: IImmutableMap<GraphNode>,
  logHandler: LogHandlerType,
  isUserSignedIn: IsUserSignedInHandlerType,
  raisedEventSucceeded?: boolean
) => {
  return async (dispatch: MDispatch, getState: () => YodaState) => {
    const state = getState();
    const dialogId = node.get('dialogId');
    const jsonPayload = getJsonPayload(jsonPayloadsSelector(state), dialogId);
    if (selectedChoice.get('values') && selectedChoice.get('values').size > 0) {
      selectedChoice.get('values').forEach((dynamicValue: IImmutableMap<DynamicChoiceValue>) => {
        if (dynamicValue.get('value') && dynamicValue.get('variableToSet')) {
          const globalVar = createImmutableMap({
            name: dynamicValue.get('variableToSet'),
            value: dynamicValue.get('value')
          });
          dispatch(updateGlobalVarsAction(createImmutableList([globalVar])));
        }
      });
    }

    let targetNodeId =
      raisedEventSucceeded === true || raisedEventSucceeded === undefined
        ? selectedChoice.get('targetNodeId')
        : selectedChoice.get('failedTargetNodeId');
    const raiseEventName = selectedChoice.get('raiseEventName');
    let targetNode = jsonPayload.get('nodes').find((n: IImmutableMap<GraphNode>) => n.get('id') === targetNodeId);
    // Skip or remove next node option if it has raiseEventName signin and the user has signed in
    targetNode = setSignInNode(targetNode, isUserSignedIn, jsonPayload);
    const metadata = jsonPayload.get('meta');
    const adHocs = generateAdhocs(metadata, getState, uId);
    // default AdHoc5 is actionreference which is null for choiceId
    adHocs[5] = JSON.stringify({
      choiceId: selectedChoice.get('id'),
      choiceName: selectedChoice.get('text').get('content')
    });
    if (
      (isNullOrUndefined(targetNode) || (!!targetNode && targetNode.size === 0)) &&
      isNullOrUndefined(raiseEventName)
    ) {
      executeLogHandler(
        LogEventName.onJsonDocumentValidationError,
        LogLevel.Error,
        LogMessageCodes.TargetNodeIdAndRaiseEventNameNull,
        uId,
        dialogId,
        logHandler,
        getState,
        adHocs
      );
    }
    executeLogHandler(
      LogEventName.onChoiceClick,
      LogLevel.Info,
      `${LogMessageCodes.ChoiceNodeClicked}: ${selectedChoice.get('text')}`,
      uId,
      dialogId,
      logHandler,
      getState,
      adHocs
    );
    const globalVars = globalVarsSelector(state);
    const nodePreCondition = checkNodePreCondition(globalVars, targetNode, jsonPayload.get('nodes'));
    if (node.get('adhocVariables')) {
      dispatch(disptachAdhocVariableUpdates(node, selectedChoice));
    }
    dispatch(
      pushTargetNodeOnRenderedNodesAction(
        uId,
        targetNode,
        selectedChoice,
        isDynamicChoice,
        choiceIndex,
        nodePreCondition.preConditionNodes
      )
    );
  };
};

export const dispatchReturnControlFromHost = (success: boolean, uId: string, dialogId: string, logHandler: LogHandlerType,
  isUserSignedIn: IsUserSignedInHandlerType) => {
  return async (dispatch: MDispatch, getState: () => YodaState) => {
    const appState = getState();
    try{
      const jsonPayload = getJsonPayload(jsonPayloadsSelector(appState), dialogId);
      const allNodes = jsonPayload.get('nodes');
      const renderedNodes = renderedNodesSelector(appState);
      const node = renderedNodes.find((n: IImmutableMap<GraphNode>) => {
        return n.get('uId') === uId;
      });
      const targetNodeId = success ? node.get('successTargetNodeId') : node.get('failedTargetNodeId');
      let targetNode = allNodes.find((n: IImmutableMap<GraphNode>) => {
        return n.get('id') === targetNodeId;
      });
  
      targetNode = setSignInNode(targetNode!, isUserSignedIn, jsonPayload);
      // Skip or remove next node option if it has raiseEventName signin and the user has signed in
      const metadata = jsonPayload.get('meta');
      const adHocs = generateAdhocs(metadata, getState, uId);
      if (isNullOrUndefined(targetNode) || (!!targetNode && targetNode.size === 0)) {
        executeLogHandler(
          LogEventName.onJsonDocumentValidationError,
          LogLevel.Error,
          LogMessageCodes.TargetNodeIdAndRaiseEventNameNull,
          uId,
          dialogId,
          logHandler,
          getState,
          adHocs
        );
      }
  
      const globalVars = globalVarsSelector(appState);
      const nodePreCondition = checkNodePreCondition(globalVars, targetNode, jsonPayload.get('nodes'));
      if (node.get('adhocVariables')) {
        dispatch(disptachAdhocVariableUpdates(node));
      }
      dispatch(pushTargetNodeFromInputNodeAction(uId, targetNode, dialogId, nodePreCondition.preConditionNodes));  
    }
    catch(e){
      const sessionId = sessionIdSelector(appState);
      executeLogHandler(
        LogEventName.onReturnControlFromHostError,
        LogLevel.Error,
        `Error in dispatchReturnControlFromHost: ${e.message}, sessionId: ${sessionId}`,
        uId,
        dialogId,
        logHandler,
        getState
      );
    }
  }
}

export const disptachAdhocVariableUpdates = (
  node: IImmutableMap<GraphNode>,
  selectedChoice?: IImmutableMap<Choice>,
  safResponse?: ISAFActionExecutionResponse,
  actionProvider?: SAFActionProvider,
  dafResponse?: DiagnosticsActionResult
) => {
  return async (dispatch: MDispatch, getState: () => YodaState) => {
    const adhocVariables = node.get('adhocVariables');
    const globalVars = globalVarsSelector(getState());
    const newAdhocVars = updateAdhocVariableValues({
      adhocVariables,
      globalVars,
      selectedChoice,
      safResponse,
      actionProvider,
      dafResponse
    });
    const renderedNodes = renderedNodesSelector(getState());
    const nodeIndex = renderedNodes.findIndex((n: IImmutableMap<GraphNode>) => {
      return n.get('uId') === node.get('uId');
    });
    dispatch(setContextVarsAction(newAdhocVars, nodeIndex));
  };
};

export const dispatchPushTargetNodeFromCondition = (
  node: IImmutableMap<GraphNode>,
  logHandler: LogHandlerType,
  isUserSignedIn: IsUserSignedInHandlerType
) => {
  return async (dispatch: MDispatch, getState: () => YodaState) => {
    const uId = node.get('uId');
    const dialogId = node.get('dialogId');
    const state = getState();
    const jsonPayload = getJsonPayload(jsonPayloadsSelector(state), dialogId);
    const targets = node.get('targets');
    let targetNodeId = node.get('defaultTargetNodeId');
    let targetNode;
    if (!!targets) {
      const globalVars = state.get('IntentGraph').get('globalVars');
      let target = targets.find((t: IImmutableMap<Target>) => {
        try {
          return evalTargetConditions(globalVars, t.get('condition'));
        } catch {
          executeLogHandler(
            LogEventName.onConditionEvaluationFailed,
            LogLevel.Error,
            `${LogMessageCodes.InternalProcessionError_ConditionEvalFailed}, condition: ${t.get('condition')}`,
            uId,
            dialogId,
            logHandler,
            getState
          );
          return false;
        }
      });
      if (!!target) {
        targetNodeId = target.get('targetNodeId');
      }
      const allNodes = jsonPayload.get('nodes');
      targetNode = allNodes.find((n: IImmutableMap<GraphNode>) => {
        return n.get('id') === targetNodeId;
      });
      targetNode = setSignInNode(targetNode!, isUserSignedIn, jsonPayload);
      // Skip or remove next node option if it has raiseEventName signin and the user has signed in
      const metadata = jsonPayload.get('meta');
      const adHocs = generateAdhocs(metadata, getState, uId);
      if (isNullOrUndefined(targetNode) || (!!targetNode && targetNode.size === 0)) {
        executeLogHandler(
          LogEventName.onJsonDocumentValidationError,
          LogLevel.Error,
          LogMessageCodes.TargetNodeIdAndRaiseEventNameNull,
          uId,
          dialogId,
          logHandler,
          getState,
          adHocs
        );
      }
    }
    const globalVars = globalVarsSelector(state);
    const nodePreCondition = checkNodePreCondition(globalVars, targetNode, jsonPayload.get('nodes'));
    if (node.get('adhocVariables')) {
      dispatch(disptachAdhocVariableUpdates(node));
    }
    dispatch(pushTargetNodeFromInputNodeAction(uId, targetNode, dialogId, nodePreCondition.preConditionNodes));
  };
};

export const dispatchEditNodeChoiceSelections = (logHandler: LogHandlerType, uId: string, dialogId: string) => {
  return async (dispatch: MDispatch, getState: () => YodaState) => {
    const renderedNodes = getState()
      .get('IntentGraph')
      .get('renderedNodes');
    //get Node for the edited choice
    const nodeIndex = renderedNodes.findIndex((n: IImmutableMap<GraphNode>) => n.get('uId') === uId);
    if (!isNullOrUndefined(nodeIndex) && nodeIndex !== -1) {
      const prevChoicesSelection = renderedNodes.slice(nodeIndex + 1, renderedNodes.count());
      dispatch(editNodeChoiceSelectionAction(uId, prevChoicesSelection.toList(), nodeIndex));
    } else {
      executeLogHandler(
        LogEventName.onJsonDocumentValidationError,
        LogLevel.Error,
        LogMessageCodes.InternalProcessionError_NodeNotFound,
        uId,
        dialogId,
        logHandler,
        getState
      );
    }
  };
};

export const dispatchCancelEditChoiceSelections = (_nodeId: string) => {
  return async (dispatch: MDispatch, getState: () => YodaState) => {
    const prevchoiceSelections = getState()
      .get('IntentGraph')
      .get('prevChoiceSelections');

    dispatch(cancelEditNodeChoiceSelectionAction(prevchoiceSelections));
  };
};

export const dispatchNodeRenderedEvent = (
  node: IImmutableMap<GraphNode>,
  nodeRenderedHandler: NodeRenderedHandlerType
) => {
  return async (_dispatch: MDispatch, getState: () => YodaState) => {
    const state = getState();
    const jsonPayload = getJsonPayload(jsonPayloadsSelector(state), node.get('dialogId'));
    const meta = jsonPayload.get('meta');
    if (!!meta) {
      const metaId = meta.get('id');
      const title = meta.get('title');
      const team = meta.get('team');

      const nodeId = node.get('id');
      const outcomeSignal = node.get('outcomeSignal');
      const tags = node.get('tags');
      const text = node.get('text') ? JSON.stringify(node.get('text').toObject()) : null;
      const nodeType = node.get('type');
      const parentUId = node.get('parentUId');
      const nodeLogProperties = getNodeLogProperties(parentUId, node, getState, meta);

      nodeRenderedHandler(metaId, title, team, nodeId, outcomeSignal, tags, nodeType, text, nodeLogProperties);
    }
  };
};

const _getTimeoutInSeconds = (nodeTimeout?: number, staticConfigTimeout?: number) => {
  if (!!nodeTimeout) {
    return nodeTimeout;
  }
  if (!!staticConfigTimeout) {
    return staticConfigTimeout;
  }
  return Constants.timeoutInSeconds;
};

export const dispatchStartDiagnosticsAction = (
  node: IImmutableMap<GraphNode>,
  diagnosticsActionHandler: DiagnosticsActionHandlerType,
  logHandler: LogHandlerType,
  isUserSignedIn: IsUserSignedInHandlerType
) => {
  return async (dispatch: MDispatch, getState: () => YodaState) => {
    let logLevel = LogLevel.Trace;
    let timerId;
    let logMessageCode = LogMessageCodes.DiagnosticsActionStarted;
    const uId = node.get('uId');
    const nodeId = node.get('id');
    const dialogId = node.get('dialogId');
    const actionType = node.get('actionType');
    const actionReference = node.get('actionReference');
    const timeoutInSeconds = node.get('timeoutSeconds');
    const staticConfigTimeout = getState()
      .get('IntentGraph')
      .get('staticConfig')
      .get('timeoutInSeconds');
    const parameters = node.get('parameters');
    const globalVars = getState()
      .get('IntentGraph')
      .get('globalVars');
    if (!!actionType && !!actionReference) {
      let params;
      if (!!parameters) {
        const paramErrorMessages: string[] = [];
        params = parameters
          .map((p: IImmutableMap<ParamOrVar>) => {
            let paramVal = p.get('value');
            if (!!globalVars) {
              const replacedVariables = findAndReplaceVariables(globalVars, paramVal, RegExpForParamOrVar);
              paramVal = replacedVariables.conditionExpression;
              paramErrorMessages.concat(replacedVariables.paramErrorMessages);
            }
            if (paramErrorMessages.length > 0) {
              executeLogHandler(
                LogEventName.onDafParamValidationError,
                LogLevel.Error,
                paramErrorMessages.join(','),
                uId,
                dialogId,
                logHandler,
                getState
              );
            }
            return {
              name: p.get('name'),
              value: paramVal
            };
          })
          .toArray();
      }
      // update only last diagnostics in list
      diagnosticsActionHandler(nodeId, actionType, actionReference, timeoutInSeconds, params);

      timerId = window.setInterval(() => {
        const renderedNodes = getState()
          .get('IntentGraph')
          .get('renderedNodes');

        const currentnode = renderedNodes.find((n: IImmutableMap<GraphNode>) => n.get('uId') === uId);
        if (currentnode) {
          const diagnosticsActionStatus = currentnode.get('diagnosticsActionStatus');
          if (diagnosticsActionStatus === DiagnosticsActionStatus.InProgress) {
            dispatch(
              dispatchDiagnosticsCompletedAction(node.get('id'), undefined, logHandler, false, isUserSignedIn, true)
            );
          }
        }
      }, _getTimeoutInSeconds(timeoutInSeconds, staticConfigTimeout) * 1000);
      const renderedNodesAfterRun = getState()
        .get('IntentGraph')
        .get('renderedNodes');
      //update only last diagnostics node after run
      const currentnodeAfterRun = renderedNodesAfterRun.findLast(
        (n: IImmutableMap<GraphNode>) => n.get('id') === nodeId
      );
      if (!currentnodeAfterRun) {
        timerId !== undefined && window.clearInterval(timerId);
      } else {
        dispatch(diagnosticsStartedAction(uId, timerId));
      }
    } else {
      logLevel = LogLevel.Error;
      logMessageCode = LogMessageCodes.FailedToStartDiagnosticsAction;
      timerId !== undefined && window.clearInterval(timerId);
    }
    executeLogHandler(
      LogEventName.onDafDiagnosticsStarted,
      logLevel,
      logMessageCode,
      uId,
      dialogId,
      logHandler,
      getState
    );
  };
};

const handleSafSchemaFetchError = async (
  retriesCount: number,
  maxRetriesCount: number,
  dispatch: MDispatch,
  getState: () => YodaState,
  diagnosticsActionHandler: GetSAFActionSchemaType,
  logHandler: LogHandlerType,
  node: IImmutableMap<GraphNode>,
  allNodes: IImmutableList<IImmutableMap<GraphNode>>,
  e?: Error,
  customErrorMessage?: string
) => {
  if (retriesCount < maxRetriesCount) {
    retriesCount += 1;
    dispatch(updateApiRetryCountAction(ExternalApi.GetSAFActionSchema, retriesCount));
    await apiRetryBackoff(2 ** retriesCount * 100);
    dispatch(dispatchStartSAFDiagnosticsAction(node, diagnosticsActionHandler, logHandler));
    return false;
  } else {
    const diagnosticsActionStatus = DiagnosticsActionStatus.Error;
    const isError = true;
    const targetNodeId = node.get('failedTargetNodeId');
    const updatedTargetNode = allNodes.find((n: IImmutableMap<GraphNode>) => {
      return n.get('id') === targetNodeId;
    });

    dispatch(safDiagnosticsCompletedAction());
    dispatch(setDiagnosticsStatusAction(DiagnosticsExecutionStatus.Failed));
    // construct error message
    let errorMessage = `SAF schema fetch failed ${customErrorMessage}`;
    if (e) {
      const eMessage = e.message;
      const eStack = e.stack;
      errorMessage = `SAF schema fetch failed. - errorMessage: ${eMessage}, errorStack: ${eStack}, retryCount: ${retriesCount}, maxRetries: ${maxRetriesCount}`;
    }

    executeLogHandler(
      LogEventName.onSAfSchemaFetchFailed,
      LogLevel.Info,
      errorMessage,
      node.get('uId'),
      node.get('dialogId'),
      logHandler,
      getState
    );
    const state = getState();
    const jsonPayload = getJsonPayload(jsonPayloadsSelector(state), node.get('dialogId'));
    const globalVars = globalVarsSelector(state);
    const nodePreCondition = checkNodePreCondition(globalVars, updatedTargetNode, jsonPayload.get('nodes'));
    dispatch(
      diagnosticsCompletedAction(
        node.get('id'),
        diagnosticsActionStatus,
        updatedTargetNode,
        isError,
        node.get('uId'),
        false,
        nodePreCondition.preConditionNodes
      )
    );
    dispatch(resetApiRetryCountAction(ExternalApi.GetSAFActionSchema));
  }
  return false;
};

export const dispatchSAFActionSchemaUpdate = (
  node: IImmutableMap<GraphNode>,
  diagnosticsActionHandler: GetSAFActionSchemaType,
  logHandler: LogHandlerType,
  result: SAFActionSchemaResponse
) => {
  return async (dispatch: MDispatch, getState: () => YodaState) => {
    const retry = getApiRetry(ExternalApi.GetSAFActionSchema, getState);
    let retriesCount = retry.get('retriesCount');
    const maxRetriesCount = retry.get('maxRetriesCount');
    const state = getState();
    const jsonPayload = getJsonPayload(jsonPayloadsSelector(state), node.get('dialogId'));
    const allNodes = jsonPayload.get('nodes');
    await handleSAFActionSchemaResult(
      result,
      dispatch,
      getState,
      diagnosticsActionHandler,
      logHandler,
      node,
      allNodes,
      retriesCount,
      maxRetriesCount
    );
  };
};

export const getApiRetry = (name: ExternalApi, getState: () => YodaState): IImmutableMap<APIRetry> | undefined => {
  const apiRetries: IImmutableList<IImmutableMap<APIRetry>> = apiRetriesSelector(getState());
  return apiRetries.find((r: IImmutableMap<APIRetry>) => r.get('name') === name);
};

export const handleSAFActionSchemaResult = async (
  result: SAFActionSchemaResponse,
  dispatch: MDispatch,
  getState: () => YodaState,
  diagnosticsActionHandler: GetSAFActionSchemaType,
  logHandler: LogHandlerType,
  node: IImmutableMap<GraphNode>,
  allNodes: IImmutableList<IImmutableMap<GraphNode>>,
  retriesCount: number,
  maxRetriesCount: number
) => {
  if (result && result.JsonSchemaForm) {
    dispatch(fetchingSafDiagnosticsAction(true, node.get('uId')));
    dispatch(loadDiagnosticResourceAction(null, createImmutableMap(result.JsonSchemaForm)));
    dispatch(dispatchSafDiagnosticRendered(node, logHandler, true));
    dispatch(resetApiRetryCountAction(ExternalApi.GetSAFActionSchema));
  } else {
    const errorMessage = 'SAF Fetching Schema returned null';
    await handleSafSchemaFetchError(
      retriesCount,
      maxRetriesCount,
      dispatch,
      getState,
      diagnosticsActionHandler,
      logHandler,
      node,
      allNodes,
      null,
      errorMessage
    );
  }
};

export const dispatchStartSAFDiagnosticsAction = (
  node: IImmutableMap<GraphNode>,
  diagnosticsActionHandler: GetSAFActionSchemaType,
  logHandler: LogHandlerType,
  useCallBacksForSAFApis?: boolean
) => {
  return async (dispatch: MDispatch, getState: () => YodaState) => {
    const state = getState();
    const jsonPayload = getJsonPayload(jsonPayloadsSelector(state), node.get('dialogId'));
    const allNodes = jsonPayload.get('nodes');
    const retry = getApiRetry(ExternalApi.GetSAFActionSchema, getState);
    let retriesCount = retry.get('retriesCount');
    const maxRetriesCount = retry.get('maxRetriesCount');
    retriesCount = retriesCount || 1;
    dispatch(fetchingSafDiagnosticsAction(false, node.get('uId')));
    // ToDo: we will remove SAFActionMap if we correct the action Ids in the converted dialogs
    const actionId = SAFActionMap[node.get('actionReference')] || node.get('actionReference');
    if (actionId) {
      diagnosticsActionHandler(actionId)
        .then(async (result: SAFActionSchemaResponse) => {
          if (!useCallBacksForSAFApis) {
            await handleSAFActionSchemaResult(
              result,
              dispatch,
              getState,
              diagnosticsActionHandler,
              logHandler,
              node,
              allNodes,
              retriesCount,
              maxRetriesCount
            );
          }
        })
        .catch(async e => {
          await handleSafSchemaFetchError(
            retriesCount,
            maxRetriesCount,
            dispatch,
            getState,
            diagnosticsActionHandler,
            logHandler,
            node,
            allNodes,
            e
          );
        });
    } else {
      const errorMessage = 'SAF Fetching Schema actionId is not found';
      await handleSafSchemaFetchError(
        retriesCount,
        maxRetriesCount,
        dispatch,
        getState,
        diagnosticsActionHandler,
        logHandler,
        node,
        allNodes,
        null,
        errorMessage
      );
    }
  };
};

const _mapDafResultsToVars = (
  resultParams: IImmutableList<IImmutableMap<ParamOrVar>>,
  node: IImmutableMap<GraphNode>
) => {
  return async (dispatch: MDispatch) => {
    const nodeVars = node.get('variables');
    if (!!nodeVars && nodeVars.size > 0 && !!resultParams && resultParams.size > 0) {
      const newVars = nodeVars.map((nVar: IImmutableMap<ParamOrVar>) => {
        let nVal = nVar.get('value');
        const nVarExp = nVal.match(RegExpForParamOrVar);
        nVarExp.forEach((exp: string) => {
          const matchingRes = resultParams.find((res: IImmutableMap<ParamOrVar>) => {
            return (
              exp.includes('}') &&
              exp.includes('{') &&
              exp.trim().toLowerCase() ===
                `{${res
                  ?.get('name')
                  .trim()
                  .toLowerCase()}}`
            );
          });
          if (!!matchingRes) {
            nVal = nVal.replace(exp, matchingRes.get('value'));
          }
        });
        return createImmutableMap({
          name: nVar.get('name'),
          value: nVal
        });
      });
      dispatch(updateGlobalVarsAction(createImmutableList(newVars.toArray())));
    }
  };
};

export const dispatchDiagnosticsCompletedAction = (
  nodeId: string,
  result: DiagnosticsActionResult,
  logHandler: LogHandlerType,
  isError: boolean = false,
  isUserSignedIn: IsUserSignedInHandlerType,
  isTimeout?: boolean
) => {
  return async (dispatch: MDispatch, getState: () => YodaState) => {
    let immutableResult: IImmutableMap<DiagnosticsActionResult>;
    const state = getState();
    const node = state
      .get('IntentGraph')
      .get('renderedNodes')
      .findLast((n: IImmutableMap<GraphNode>) => n.get('id') === nodeId) as IImmutableMap<GraphNode>;
    if (node) {
      const uId = node.get('uId');
      const dialogId = node.get('dialogId');
      window.clearInterval(node.get('timerId'));
      if (node.get('diagnosticsActionStatus') === DiagnosticsActionStatus.InProgress) {
        let diagnosticsActionStatus = DiagnosticsActionStatus.CompletedSuccessfully;
        let targetNode;
        let targetNodeId = node.get('defaultTargetNodeId');
        const jsonPayload = getJsonPayload(jsonPayloadsSelector(state), dialogId);
        const allNodes = jsonPayload.get('nodes');
        if (!!isError) {
          executeLogHandler(
            LogEventName.onDafDiagnosticsFailure,
            LogLevel.Error,
            LogMessageCodes.DiagnosticsActionFailed,
            uId,
            dialogId,
            logHandler,
            getState
          );
          diagnosticsActionStatus = DiagnosticsActionStatus.Error;
          targetNodeId = node.get('failedTargetNodeId');
        } else if (!!isTimeout) {
          executeLogHandler(
            LogEventName.onDafDiagnosticsActionTimeout,
            LogLevel.Error,
            LogMessageCodes.DiagnosticsActionTimeout,
            uId,
            dialogId,
            logHandler,
            getState
          );
          diagnosticsActionStatus = DiagnosticsActionStatus.Error;
          targetNodeId = node.get('failedTargetNodeId');
        } else {
          if (!!result) {
            immutableResult = deserializeToImmutableMap(DiagnosticsActionResult, result);
            if (immutableResult.get('IsSuccess')) {
              executeLogHandler(
                LogEventName.onDafDiagnosticsCompleted,
                LogLevel.Info,
                LogMessageCodes.DiagnosticsActionResponseWithIsSuccessTrue,
                uId,
                dialogId,
                logHandler,
                getState
              );
              const resultParams = immutableResult.get('Results');
              if (!!resultParams) {
                await dispatch(_mapDafResultsToVars(resultParams, node));
              }
              // add context variables
              if (node.get('adhocVariables')) {
                dispatch(disptachAdhocVariableUpdates(node, undefined, undefined, undefined, result));
              }

              const targets = node.get('targets');
              if (!!targets) {
                const globalVars = getState()
                  .get('IntentGraph')
                  .get('globalVars');
                targetNode = targets.find((target: IImmutableMap<Target>) => {
                  try {
                    return evalTargetConditions(globalVars, target.get('condition'));
                  } catch {
                    executeLogHandler(
                      LogEventName.onConditionEvaluationFailed,
                      LogLevel.Error,
                      `${LogMessageCodes.InternalProcessionError_ConditionEvalFailed}, condition: ${target.get(
                        'condition'
                      )}`,
                      uId,
                      dialogId,
                      logHandler,
                      getState
                    );
                    return false;
                  }
                });
                if (!!targetNode) {
                  targetNodeId = targetNode.get('targetNodeId');
                }
              }
            } else {
              executeLogHandler(
                LogEventName.onDafDiagnosticsFailure,
                LogLevel.Trace,
                LogMessageCodes.DiagnosticsActionResponseWithIsSuccessFalse,
                uId,
                dialogId,
                logHandler,
                getState
              );
              diagnosticsActionStatus = DiagnosticsActionStatus.Error;
              targetNodeId = node.get('failedTargetNodeId');
            }
          } else {
            executeLogHandler(
              LogEventName.onDafDiagnosticsActionResultIsNull,
              LogLevel.Error,
              LogMessageCodes.DiagnosticsActionResultResponseIsNull,
              uId,
              dialogId,
              logHandler,
              getState
            );
            diagnosticsActionStatus = DiagnosticsActionStatus.Error;
            targetNodeId = node.get('failedTargetNodeId');
          }
        }

        // find the target node from all nodes
        targetNode = allNodes.find((n: IImmutableMap<GraphNode>) => {
          return n.get('id') === targetNodeId;
        });
        let updatedTargetNode;
        if (!!targetNode) {
          updatedTargetNode = targetNode.withMutations((mutableTargetNode: IImmutableMap<GraphNode>) => {
            if (mutableTargetNode.get('dynamicChoices')) {
              const dynamicChoiceTargetNodeId = mutableTargetNode.get('dynamicChoiceTargetNodeId');
              const dynamicChoicesList = immutableResult.get('DynamicChoices').map((dynamicChoice: string) => {
                const text = createImmutableMap({
                  contentType: ContentType.EmbeddedPlainText,
                  content: dynamicChoice,
                  contentText: '',
                  contentTitle: ''
                } as TextField);
                return createImmutableMap({
                  text: text,
                  raiseEventName: '',
                  targetNodeId: dynamicChoiceTargetNodeId
                } as Choice);
              });
              mutableTargetNode.set('dynamicChoicesList', createImmutableList(dynamicChoicesList.toArray()));
            }
            return mutableTargetNode;
          });
        } else {
          const state = getState();
          const sessionId = sessionIdSelector(state);
          const metadata = jsonPayload.get('meta');
          const adHocs = generateAdhocs(metadata, getState, uId);
          adHocs[3] = targetNodeId;
          logHandler(
            LogEventName.onJsonDocumentValidationError,
            LogLevel.Error,
            sessionId,
            LogMessageCodes.TargetNodeNotFound,
            adHocs
          );
        }
        updatedTargetNode = setSignInNode(updatedTargetNode, isUserSignedIn, jsonPayload);
        const globalVars = globalVarsSelector(getState());
        const nodePreCondition = checkNodePreCondition(globalVars, updatedTargetNode, jsonPayload.get('nodes'));
        dispatch(
          diagnosticsCompletedAction(
            node.get('id'),
            diagnosticsActionStatus,
            updatedTargetNode,
            isError,
            uId,
            false,
            nodePreCondition.preConditionNodes
          )
        );
      } else {
        executeLogHandler(
          LogEventName.onDafDiagnosticsFailure,
          LogLevel.Warning,
          LogMessageCodes.DiagnosticsResultHandlerCalledMultipleTimesOrAfterTimeout,
          uId,
          dialogId,
          logHandler,
          getState
        );
      }
    }
  };
};

export const dispatchSafDiagnosticRendered = (
  node: IImmutableMap<GraphNode>,
  logHandler: LogHandlerType,
  successFullyRendered: boolean,
  diagnosticUId?: string
) => {
  return async (dispatch: MDispatch, getState: () => YodaState) => {
    const state = getState();
    const jsonPayload = getJsonPayload(jsonPayloadsSelector(state), node.get('dialogId'));
    const allNodes = jsonPayload.get('nodes');
    // find the target node from all nodes
    const targetNodeId = successFullyRendered ? node.get('successTargetNodeId') : node.get('failedTargetNodeId');
    const targetNode = allNodes.find((n: IImmutableMap<GraphNode>) => {
      return n.get('id') === targetNodeId;
    });
    const globalVars = globalVarsSelector(getState());
    const nodePreCondition = checkNodePreCondition(globalVars, targetNode, jsonPayload.get('nodes'));
    dispatch(
      safDiagnosticRenderedAction(
        node.get('uId'),
        targetNode,
        node.get('id'),
        diagnosticUId,
        successFullyRendered,
        nodePreCondition.preConditionNodes
      )
    );
    executeLogHandler(
      LogEventName.onSafDiagnosticRendered,
      LogLevel.Info,
      `render status: ${successFullyRendered}`,
      node.get('uId'),
      node.get('dialogId'),
      logHandler,
      getState
    );
  };
};

export const dispatchFetchSafDiagnosticsAction = (
  node: IImmutableMap<GraphNode>,
  diagnosticsActionHandler: SafDiagnosticsRendererType,
  logHandler: LogHandlerType,
  diagForm: IImmutableMap<FormV2>
) => {
  return async (dispatch: MDispatch, getState: () => YodaState) => {
    if (node && diagnosticsActionHandler && node.get('payload')) {
      const solutionId = node.get('payload');
      if (!diagForm || !diagForm.get('Identifier') || diagForm.get('Identifier') !== solutionId) {
        const solutionType = ContentType.ServerDiagnostic;
        const state = getState();
        const jsonPayload = getJsonPayload(jsonPayloadsSelector(state), node.get('dialogId'));
        const meta = jsonPayload.get('meta');
        const sessionId = sessionIdSelector(state);
        if (node.get('diagnosticUId')) {
          dispatch(fetchingSafDiagnosticsAction(true, node.get('uId')));
          dispatch(dispatchSafDiagnosticRendered(node, logHandler, true, node.get('diagnosticUId')));
        } else {
          dispatch(fetchingSafDiagnosticsAction(false, node.get('uId')));
          diagnosticsActionHandler(solutionType, solutionId)
            .then((data: DiagnosticsInitResponse) => {
              if (!!data) {
                dispatch(loadDiagnosticResourceAction(createImmutableMap(data)));
                dispatch(dispatchSafDiagnosticRendered(node, logHandler, true, data.UId));
                logHandler(
                  LogEventName.onDiagnosticResourceReturned,
                  LogLevel.Info,
                  sessionId,
                  'Diagnostic resource returned',
                  generateAdhocs(meta, undefined, undefined, node)
                );
              } else {
                dispatch(dispatchSafDiagnosticRendered(node, logHandler, false, data.UId));
              }
              dispatch(fetchingSafDiagnosticsAction(true, node.get('uId')));
            })
            .catch((e: Error) => {
              dispatch(fetchingSafDiagnosticsAction(true, node.get('uId')));
              logHandler(
                LogEventName.onDiagnosticResourceRetrivalFailure,
                LogLevel.Info,
                sessionId,
                `Error retirieving diagnostic resource errorMessage: ${e.message}, errorStack: ${JSON.stringify(
                  e.stack
                )}`,
                generateAdhocs(meta, undefined, undefined, node)
              );
              dispatch(dispatchSafDiagnosticRendered(node, logHandler, false));
            });
        }
      } else {
        dispatch(dispatchSafDiagnosticRendered(node, logHandler, true));
        dispatch(fetchingSafDiagnosticsAction(true, node.get('uId')));
      }
    }
  };
};

export const dispatchPartnerSolutionRenderedAction = (
  node: IImmutableMap<GraphNode>,
  partnerSolutionRenderedHandler: PartnerSolutionRendererType,
  logHandler: LogHandlerType,
) => {
  return async (_dispatch: MDispatch, _getState: () => YodaState) => {
    if(node && node.get('text')){
      const solutionType: string = node.get('text').get('contentType');
      const solutionId: string = node.get('text').get('content');
      const nodeId: string = node.get('id');
      const uId: string = node.get('uId');
      const dialogId: string = node.get('dialogId');
      console.log('rendering partner node', [solutionType, solutionId]);
      partnerSolutionRenderedHandler(solutionType, solutionId, nodeId, uId, dialogId)
    }
  }
};

export const dispatchDiagnosticsFormComponentUpdate = (
  componentKey: string,
  updatedComponent: string | number | boolean | Date | null | undefined
) => {
  return async (dispatch: MDispatch) => {
    dispatch(updateDiagnosticsFormComponentAction(componentKey, updatedComponent));
  };
};

const getDiagnosticsFormValues = (getState: () => YodaState): IImmutableMap<DiagnosticsStartRequest> => {
  const diagnosticsForm = getState()
    .get('IntentGraph')
    .get('diagnosticsFormModel');
  const diagnosticsData = new DiagnosticsStartRequest();
  if (diagnosticsForm) {
    const jsonFormValues = diagnosticsForm.get('JsonFormValues');
    if (jsonFormValues) {
      diagnosticsData.CorrelationId = diagnosticsForm.get('CorrelationId');
      diagnosticsData.TenantId = diagnosticsForm.get('TenantId');
      diagnosticsData.Identifier = diagnosticsForm.get('Identifier');
      diagnosticsData.Locale = diagnosticsForm.get('Locale');
      diagnosticsData.JsonFormValues = JSON.stringify(!!jsonFormValues ? jsonFormValues.toObject() : {});
    }
  }

  return createImmutableMap(diagnosticsData);
};

const continueDiagnosticRun = (getState: () => YodaState): boolean => {
  const diagnosticsResponsePreviousState = diagnosticsResponseSelector(getState());
  return !diagnosticsResponsePreviousState.get('AbandonCurrentRun');
};

export const dispatchStartDiagnostics = (
  StartDiagnosticsHandler: StartDiagnosticsType,
  GetDiagnosticsResultHandler: GetDiagnosticsResultType,
  logHandler: LogHandlerType,
  uId: string,
  dialogId: string
) => {
  return async (dispatch: MDispatch, getState: () => YodaState) => {
    try {
      const diagnosticsData = getDiagnosticsFormValues(getState);
      dispatch(setDiagnosticsStatusAction(DiagnosticsExecutionStatus.StartRequested));
      dispatch(abandonDiagnosticsRunAction(false));
      dispatch(diagnosticsStartedAction(uId, undefined));
      const r = await StartDiagnosticsHandler(diagnosticsData.toObject());
      if (!!r && continueDiagnosticRun(getState)) {
        const response = createImmutableMap(r);
        dispatch(startDiagnosticsRunAction(response));
        let timerId;
        if (response.get('Status') === DiagnosticsExecutionStatus.Executing) {
          dispatch(incrementDiagnosticsRunProgressAction());
          const state = getState();
          const timerDelay = state
            .get('IntentGraph')
            .get('diagnosticsTimer')
            .get('Delay');
          timerId = window.setInterval(() => {
            dispatch(
              dispatchGetDiagnosticsResult(
                GetDiagnosticsResultHandler,
                state.get('IntentGraph').get('diagnosticsResponse'),
                logHandler,
                uId,
                dialogId
              )
            );
          }, timerDelay);
          dispatch(startDiagnosticsTimerAction(timerId));
          executeLogHandler(
            LogEventName.onStartDiagnosticsSucceeded,
            LogLevel.Info,
            'StartDiagnostics succeeded.',
            uId,
            dialogId,
            logHandler,
            getState
          );
        } else {
          window.clearInterval(timerId);
          dispatch(stopDiagnosticsTimerAction());
          dispatch(setDiagnosticsStatusAction(DiagnosticsExecutionStatus.Failed));
          executeLogHandler(
            LogEventName.onStartDiagnosticsFailed,
            LogLevel.Info,
            'StartDiagnostics failed.',
            uId,
            dialogId,
            logHandler,
            getState
          );
        }
      }
    } catch (e) {
      dispatch(safDiagnosticsCompletedAction());
      if (continueDiagnosticRun(getState)) {
        dispatch(setDiagnosticsStatusAction(DiagnosticsExecutionStatus.Failed));
        executeLogHandler(
          LogEventName.onStartDiagnosticsFailed,
          LogLevel.Info,
          `StartDiagnostics failed. - errorMessage: ${e.message}, errorStack: ${JSON.stringify(e.stack)}`,
          uId,
          dialogId,
          logHandler,
          getState
        );
      }
    }
  };
};

const populateContextVariables = (getState: () => YodaState, node: IImmutableMap<GraphNode>, dispatch: MDispatch) => {
  const state = getState();
  const diagnosticsForm = state.get('IntentGraph').get('diagnosticsFormModel');
  const globalVars = state.get('IntentGraph').get('globalVars');
  let contextVariablesList = diagnosticsForm.get('ContextVariables');
  // convert from map to SAFContextVariable[] for easier consumption by hosts
  let contextVariables = contextVariablesList
    ? contextVariablesList.toArray().map((value: IImmutableMap<SAFContextVariable>) => {
        return {
          Name: value.get('Name'),
          ValueProvider: value.get('ValueProvider')
        } as SAFContextVariable;
      })
    : ([] as SAFContextVariable[]);
  let globalVariables = diagnosticsForm.get('GlobalVariables');
  let parameters: { [key: string]: any } = {};
  if (diagnosticsForm) {
    const jsonFormValues = diagnosticsForm.get('JsonFormValues');
    if (jsonFormValues) {
      parameters = jsonFormValues.toObject();
      if (globalVariables) {
        globalVariables.forEach((variable: string) => {
          if (variable) {
            globalVars.find((param: IImmutableMap<ParamOrVar>) => {
              if (param.get('name').toLowerCase() === variable.toLowerCase()) {
                parameters[variable] = param.get('value');
                return true;
              }
              return false;
            });
          }
        });
      }
    }
  }
  if (parameters) {
    const updatedGlobalVars: IImmutableMap<ParamOrVar>[] = [];
    Object.keys(parameters).forEach((k: string) => {
      updatedGlobalVars.push(
        createImmutableMap({
          name: k,
          value: parameters[k]
        })
      );
    });
    // add diagnostic form paramters to globalVar
    if (updatedGlobalVars.length > 0) {
      dispatch(updateGlobalVarsAction(createImmutableList(updatedGlobalVars)));
    }
  }
  return {
    contextVariables: contextVariables,
    parameters: parameters
  };
};

const apiRetryBackoff = (ms: number) => new Promise(res => setTimeout(res, ms));

const handleSafActionError = async (
  retriesCount: number,
  maxRetriesCount: number,
  dispatch: MDispatch,
  getState: () => YodaState,
  executeSAFActionHandler: ExecuteSAFActionType,
  logHandler: LogHandlerType,
  node: IImmutableMap<GraphNode>,
  e?: Error,
  customErrorMessage?: string
) => {
  if (retriesCount < maxRetriesCount) {
    retriesCount += 1;
    dispatch(updateApiRetryCountAction(ExternalApi.ExecuteSAFAction, retriesCount));
    await apiRetryBackoff(2 ** retriesCount * 100);
    dispatch(dispatchStartSAFAction(executeSAFActionHandler, logHandler, node));
    return false;
  } else {
    const diagnosticsActionStatus = DiagnosticsActionStatus.Error;
    const isError = true;
    const targetNodeId = node.get('failedTargetNodeId');
    const updatedTargetNode = getTargetNodeFromCondition(
      node.get('failureTargets'),
      targetNodeId,
      node.get('uId'),
      node.get('dialogId'),
      getState,
      logHandler
    );

    dispatch(safDiagnosticsCompletedAction());
    dispatch(setDiagnosticsStatusAction(DiagnosticsExecutionStatus.Failed));
    // construct error message
    let errorMessage = `SAF action failed ${customErrorMessage}`;
    if (e) {
      const eMessage = e.message;
      const eStack = e.stack;
      errorMessage = `SAF action failed. - errorMessage: ${eMessage}, errorStack: ${eStack}, retryCount: ${retriesCount}, maxRetries: ${maxRetriesCount}`;
    }

    executeLogHandler(
      LogEventName.onSAfActionRunFailed,
      LogLevel.Info,
      errorMessage,
      node.get('uId'),
      node.get('dialogId'),
      logHandler,
      getState
    );
    dispatch(resetApiRetryCountAction(ExternalApi.ExecuteSAFAction));
    const state = getState();
    const jsonPayload = getJsonPayload(jsonPayloadsSelector(state), node.get('dialogId'));
    const globalVars = globalVarsSelector(state);
    const nodePreCondition = checkNodePreCondition(globalVars, updatedTargetNode, jsonPayload.get('nodes'));
    dispatch(
      diagnosticsCompletedAction(
        node.get('id'),
        diagnosticsActionStatus,
        updatedTargetNode,
        isError,
        node.get('uId'),
        false,
        nodePreCondition.preConditionNodes
      )
    );
  }
  return false;
};

const getTargetNodeFromCondition = (
  targets: IImmutableList<IImmutableMap<Target>>,
  targetNodeId: string,
  uId: string,
  dialogId: string,
  getState: () => YodaState,
  logHandler: LogHandlerType
) => {
  const state = getState();
  const jsonPayload = getJsonPayload(jsonPayloadsSelector(state), dialogId);
  const allNodes = jsonPayload.get('nodes');
  const globalVars = state.get('IntentGraph').get('globalVars');
  if (targets) {
    const target = targets.find((t: IImmutableMap<Target>) => {
      try {
        return evalTargetConditions(globalVars, t.get('condition'));
      } catch {
        executeLogHandler(
          LogEventName.onConditionEvaluationFailed,
          LogLevel.Error,
          `${LogMessageCodes.InternalProcessionError_ConditionEvalFailed}, condition: ${t.get('condition')}`,
          uId,
          dialogId,
          logHandler,
          getState
        );
        return false;
      }
    });
    // change default targetNode id if a condition has been satisfied in targets
    if (!!target) {
      targetNodeId = target.get('targetNodeId');
    }
  }
  // find the target node from all nodes
  const targetNode = allNodes.find((n: IImmutableMap<GraphNode>) => {
    return n.get('id') === targetNodeId;
  });
  return targetNode;
};

const getContextValues = (returnedValues: object): any[] => {
  let values: any[] = [];
  let parsedValues;
  if (typeof returnedValues === 'string') {
    parsedValues = getJson(returnedValues);
  } else {
    parsedValues = returnedValues;
  }
  // v1 Values was an array, for backward compatibility, return is an array
  if (!Array.isArray(parsedValues)) {
    values = [parsedValues];
  } else {
    values = parsedValues;
  }
  return values;
};

export const dispatchSAFActionExecutionUpdate = (
  node: IImmutableMap<GraphNode>,
  executeSAFActionHandler: ExecuteSAFActionType,
  logHandler: LogHandlerType,
  result: ISAFActionExecutionResponse
) => {
  let tryingAgain = false;
  let updatedTargetNode: IImmutableMap<GraphNode>;
  let isError = false;
  let diagnosticsActionStatus = DiagnosticsActionStatus.InProgress;
  const targetNodeId = node.get('defaultTargetNodeId');
  return async (dispatch: MDispatch, getState: () => YodaState) => {
    const retry = getApiRetry(ExternalApi.ExecuteSAFAction, getState);
    let retriesCount = retry.get('retriesCount');
    const maxRetriesCount = retry.get('maxRetriesCount');
    try {
      const processedResults = await handleSAFActionExecutionResult(
        result,
        dispatch,
        getState,
        executeSAFActionHandler,
        logHandler,
        node,
        retriesCount,
        maxRetriesCount
      );
      isError = processedResults.isError;
      tryingAgain = processedResults.tryingAgain;
      updatedTargetNode = processedResults.updatedTargetNode;
      diagnosticsActionStatus = processedResults.diagnosticsActionStatus;
    } catch (e) {
      // exception caught error case
      isError = true;
      tryingAgain = await handleSafActionError(
        retriesCount,
        maxRetriesCount,
        dispatch,
        getState,
        executeSAFActionHandler,
        logHandler,
        node
      );
    }
    if (!tryingAgain && !isError) {
      if (!updatedTargetNode) {
        updatedTargetNode = getTargetNodeFromCondition(
          node.get('defaultTargets'),
          targetNodeId,
          node.get('uId'),
          node.get('dialogId'),
          getState,
          logHandler
        );
      }
      const state = getState();
      const jsonPayload = getJsonPayload(jsonPayloadsSelector(state), node.get('dialogId'));
      const globalVars = globalVarsSelector(state);
      const nodePreCondition = checkNodePreCondition(globalVars, updatedTargetNode, jsonPayload.get('nodes'));
      dispatch(setDiagnosticsStatusAction(DiagnosticsExecutionStatus.Completed));
      dispatch(
        diagnosticsCompletedAction(
          node.get('id'),
          diagnosticsActionStatus,
          updatedTargetNode,
          isError,
          node.get('uId'),
          false,
          nodePreCondition.preConditionNodes
        )
      );
    }
  };
};

const handleSAFActionExecutionResult = async (
  r: ISAFActionExecutionResponse,
  dispatch: MDispatch,
  getState: () => YodaState,
  executeSAFActionHandler: ExecuteSAFActionType,
  logHandler: LogHandlerType,
  node: IImmutableMap<GraphNode>,
  retriesCount: number,
  maxRetriesCount: number
) => {
  let tryingAgain = false;
  let targetNode;
  let slotText: string;
  let slotValue: any;
  let updatedTargetNode: IImmutableMap<GraphNode>;
  let isError = false;
  let diagnosticsActionStatus = DiagnosticsActionStatus.InProgress;
  let targetNodeId = node.get('defaultTargetNodeId');
  const actionId = node.get('actionReference');
  const nodeId = node.get('id');
  if (!!r) {
    let contextVariables: IImmutableMap<{
      name: string;
      value: any;
    }>[];
    let values: any[] = [];
    diagnosticsActionStatus = DiagnosticsActionStatus.CompletedSuccessfully;
    const state = getState();
    const diagnosticsForm = state.get('IntentGraph').get('diagnosticsFormModel');
    // defaulting to SAFActionProvider.SAF for backward compatibility
    let actionProvider =
      diagnosticsForm && diagnosticsForm.get('ActionProvider')
        ? diagnosticsForm.get('ActionProvider')
        : SAFActionProvider.SAF.toString();
    actionProvider = actionProvider.toLowerCase();
    // SAF v1 result
    if (actionProvider === SAFActionProvider.SAF.toLowerCase() && (!!r.context || r.Value.context)) {
      let context = r.context as { [key: string]: object };
      context = context || (r.Value.context as { [key: string]: object });
      values = getContextValues(context.Values);
      // adding context properties to globalVar list
      // Todo: check if should add Header and StatusCode
      contextVariables = Object.keys(context).map((key: string) => {
        if (key && context.hasOwnProperty(key)) {
          return createImmutableMap({
            name: key,
            value: context[key] as any
          });
        }
      });
    }
    // SAF v2 result
    else if (actionProvider == SAFActionProvider.KnowMe.toLowerCase()) {
      values = getContextValues(r.Value);
      contextVariables = [
        // Todo: check if these values should be lowercase
        createImmutableMap({
          name: actionId + '-Headers',
          value: r.Headers as any
        }),
        createImmutableMap({
          name: actionId + '-StatusCode',
          value: r.StatusCode as any
        }),
        // leaving values without actionId for backward compatability
        createImmutableMap({
          name: 'Values',
          value: r.Value as any
        }),
        createImmutableMap({
          name: actionId + '-Values',
          value: r.Value as any
        }),
        // adding item_count, since SAF provider was setting it for most responses
        createImmutableMap({
          name: actionId + '-item_count',
          value: values ? values.length : 0
        })
      ];
    }
    await dispatch(updateGlobalVarsAction(createImmutableList(contextVariables)));
    // add context variables
    if (node.get('adhocVariables')) {
      dispatch(disptachAdhocVariableUpdates(node, undefined, r, actionProvider as SAFActionProvider));
    }
    targetNode = getTargetNodeFromCondition(
      node.get('targets'),
      targetNodeId,
      node.get('uId'),
      node.get('dialogId'),
      getState,
      logHandler
    );
    if (!!targetNode) {
      updatedTargetNode = targetNode.withMutations((mutableTargetNode: IImmutableMap<GraphNode>) => {
        if (mutableTargetNode.get('dynamicChoices')) {
          // we will check slotValue across dynamicChoiceTargets and grab the targetNodeId
          let dynamicChoicesList: IImmutableMap<Choice>[] = [];
          let dynamicValues: IImmutableMap<DynamicVariableValue>[] = [];
          /** temporaryDynamicValuesPool added to be used for dynamicChoiceTargetId reading, for example, if we have 
               * "dynamicChoiceVariables": {
                          "text": "ProductTitle",
                          "values": [
                            {
                                "targetValue": true,
                                "value": "Id",
                                "variableToSet": "SelectedSubscription"....SelectedOutput, since we have targetValue: true
                            },
                              {
                                "targetValue": false,
                                "value": "ProductTitle",
                                "variableToSet": "SelectedTitle"
                            }
                        ]
                      }
            * and we have a logic inside dynamicChoiceTargets as 
                    "dynamicChoiceTargets": [
                      {
                        "condition": "'{SelectedOutput}'== 'some Id' || '{SelectedTitle}' === 'some title'",
                        "targetNodeId": "a625b4d6-0229-470a-abe1-d1dd1c5268fa"
                      },]
            * SelectedOutput/orderSelection are keywords meaning selected value and will be compared with the targetValue value. 
            * Creating temporaryDynamicValuesPool for replacing values that are not targetValues inside conditions, example {SelectedTitle}
              *  */

          if (mutableTargetNode.get('rootObjectPath')) {
            let rootObjectPath = mutableTargetNode.get('rootObjectPath').toLowerCase();
            // snip values
            rootObjectPath = rootObjectPath.startsWith('values.')
              ? rootObjectPath.slice(7, rootObjectPath.length)
              : rootObjectPath;
            if (rootObjectPath) {
              values = readValue(mutableTargetNode.get('rootObjectPath'), r.Value);
            }
          }
          if (values && mutableTargetNode.get('dynamicChoiceTargets')) {
            values.forEach((value: { [key: string]: object | string }) => {
              const temporaryDynamicValuesPool: IImmutableMap<ParamOrVar>[] = [];
              if (mutableTargetNode.get('dynamicChoiceVariables')) {
                const dynamicChoiceVariables = mutableTargetNode.get('dynamicChoiceVariables');
                // values to be set in globalStore on dynamic choice click
                const dynamicSlotValues = dynamicChoiceVariables.get('values');
                if (dynamicSlotValues && dynamicSlotValues.size > 0) {
                  dynamicSlotValues.forEach((dynamicSlotValue: IImmutableMap<DynamicVariableValue>) => {
                    const valueFromResponse = readValue(dynamicSlotValue.get('value'), value);
                    if (dynamicSlotValue.get('targetValue')) {
                      slotValue = readValue(dynamicSlotValue.get('value'), value);
                      temporaryDynamicValuesPool.push(
                        createImmutableMap({
                          name: 'orderselection',
                          value: valueFromResponse
                        })
                      );
                      temporaryDynamicValuesPool.push(
                        createImmutableMap({
                          name: 'selectedoutput',
                          value: valueFromResponse
                        })
                      );
                    }
                    temporaryDynamicValuesPool.push(
                      createImmutableMap({
                        name: dynamicSlotValue.get('variableToSet'),
                        value: valueFromResponse
                      })
                    );
                    dynamicValues.push(
                      createImmutableMap({
                        value: valueFromResponse,
                        variableToSet: dynamicSlotValue.get('variableToSet')
                      } as DynamicVariableValue)
                    );
                  });
                }
                slotText = readValue(dynamicChoiceVariables.get('text'), value) as string;
              }
              mutableTargetNode.get('dynamicChoiceTargets').find((t: IImmutableMap<Target>) => {
                let conditionTargetNodeId;
                let conditionExpression = t.get('condition') || '';
                const globalVars = globalVarsSelector(getState());
                conditionExpression = findAndReplaceVariables(
                  globalVars,
                  conditionExpression,
                  RegExpForParamOrVar,
                  undefined,
                  createImmutableList(temporaryDynamicValuesPool)
                ).conditionExpression;
                if (!!slotValue && typeof slotValue === 'string') {
                  slotValue = slotValue.toLowerCase();
                }
                conditionExpression =
                  slotValue !== undefined
                    ? conditionExpression
                        .replace('{orderselection}', `${slotValue}` as string)
                        .replace('{selectedoutput}', `${slotValue}` as string)
                    : conditionExpression;
                // converted schema will have 'selected' to symbolize the selected node, usually only one condition for all in this case
                if (
                  conditionExpression &&
                  // eslint-disable-next-line no-eval
                  (eval(conditionExpression) || conditionExpression.toLowerCase().includes("=='selected'"))
                ) {
                  conditionTargetNodeId = t.get('targetNodeId');
                  const text = createImmutableMap({
                    contentType: ContentType.EmbeddedPlainText,
                    content: slotText,
                    contentText: '',
                    contentTitle: ''
                  } as TextField);
                  const dynamicChoice = createImmutableMap(({
                    text: text,
                    raiseEventName: '',
                    targetNodeId: conditionTargetNodeId,
                    values: dynamicValues && dynamicValues.length > 0 ? createImmutableList(dynamicValues) : null
                  } as unknown) as Choice);
                  dynamicChoicesList.push(dynamicChoice);
                  return true;
                }
                return false;
              });
            });
            const y = createImmutableList(dynamicChoicesList);
            mutableTargetNode = mutableTargetNode.set('dynamicChoicesList', y);
          }
        }
        return mutableTargetNode;
      });
    } else {
      // target node not found error case
      const errorMessage = 'Saf action target node not found after execution';
      isError = true;
      tryingAgain = await handleSafActionError(
        retriesCount,
        maxRetriesCount,
        dispatch,
        getState,
        executeSAFActionHandler,
        logHandler,
        node,
        null,
        errorMessage
      );
    }
    dispatch(abandonDiagnosticsRunAction(false));
    dispatch(diagnosticsStartedAction(nodeId, undefined));
  }
  return { updatedTargetNode, tryingAgain, isError, diagnosticsActionStatus };
};

export const dispatchStartSAFAction = (
  executeSAFActionHandler: ExecuteSAFActionType,
  logHandler: LogHandlerType,
  node: IImmutableMap<GraphNode>
) => {
  return async (dispatch: MDispatch, getState: () => YodaState) => {
    let tryingAgain = false;
    const retry = getApiRetry(ExternalApi.ExecuteSAFAction, getState);
    let retriesCount = retry.get('retriesCount');
    const maxRetriesCount = retry.get('maxRetriesCount');
    const state = getState();
    const useCallBacksForSAFApis = enableUsingCallBacksForSAFApisSelector(state);
    const targetNodeId = node.get('defaultTargetNodeId');
    let updatedTargetNode: IImmutableMap<GraphNode>;
    let isError = false;
    let diagnosticsActionStatus = DiagnosticsActionStatus.InProgress;
    try {
      dispatch(abandonDiagnosticsRunAction(false));
      dispatch(setDiagnosticsStatusAction(DiagnosticsExecutionStatus.StartRequested));
      // ToDo: we will remove SAFActionMap if we correct the action Ids in the converted dialogs
      const actionId = SAFActionMap[node.get('actionReference')] || node.get('actionReference');
      if (actionId) {
        const paramAndContext = populateContextVariables(getState, node, dispatch);
        let r = await executeSAFActionHandler(actionId, paramAndContext.parameters, paramAndContext.contextVariables);
        // if there is content returned from SAF action
        if (!useCallBacksForSAFApis) {
          r = r as ISAFActionExecutionResponse;
          const processedResults = await handleSAFActionExecutionResult(
            r,
            dispatch,
            getState,
            executeSAFActionHandler,
            logHandler,
            node,
            retriesCount,
            maxRetriesCount
          );
          isError = processedResults.isError;
          tryingAgain = processedResults.tryingAgain;
          updatedTargetNode = processedResults.updatedTargetNode;
          diagnosticsActionStatus = processedResults.diagnosticsActionStatus;
        }
      } else {
        // action Id missing error case
        const errorMessage = 'actionId missing';
        isError = true;
        tryingAgain = await handleSafActionError(
          retriesCount,
          maxRetriesCount,
          dispatch,
          getState,
          executeSAFActionHandler,
          logHandler,
          node,
          null,
          errorMessage
        );
      }
    } catch (e) {
      // exception caught error case
      isError = true;
      tryingAgain = await handleSafActionError(
        retriesCount,
        maxRetriesCount,
        dispatch,
        getState,
        executeSAFActionHandler,
        logHandler,
        node
      );
    }
    if (!tryingAgain && !isError && !useCallBacksForSAFApis) {
      if (!updatedTargetNode) {
        updatedTargetNode = getTargetNodeFromCondition(
          node.get('defaultTargets'),
          targetNodeId,
          node.get('uId'),
          node.get('dialogId'),
          getState,
          logHandler
        );
      }
      dispatch(setDiagnosticsStatusAction(DiagnosticsExecutionStatus.Completed));
      const state = getState();
      const jsonPayload = getJsonPayload(jsonPayloadsSelector(state), node.get('dialogId'));
      const globalVars = globalVarsSelector(state);
      const nodePreCondition = checkNodePreCondition(globalVars, updatedTargetNode, jsonPayload.get('nodes'));
      dispatch(
        diagnosticsCompletedAction(
          node.get('id'),
          diagnosticsActionStatus,
          updatedTargetNode,
          isError,
          node.get('uId'),
          false,
          nodePreCondition.preConditionNodes
        )
      );
    }
  };
};

export const dispatchGetDiagnosticsResult = (
  GetDiagnosticsResultHandler: GetDiagnosticsResultType,
  diagnoticsRes: IImmutableMap<DiagnosticsProgressResponse>,
  logHandler: LogHandlerType,
  uId: string,
  dialogId: string
) => {
  return async (dispatch: MDispatch, getState: () => YodaState) => {
    const state = getState();
    const timer = state.get('IntentGraph').get('diagnosticsTimer');
    const timerId = timer.get('IntervalId');
    try {
      const progress = state.get('IntentGraph').get('diagnosticsProgress');
      if (progress < timer.get('MaxIterations')) {
        dispatch(incrementDiagnosticsRunProgressAction());
        const diagnosticsProgressRequest: DiagnosticsProgressRequest = new DiagnosticsProgressRequest();
        diagnosticsProgressRequest.Identifier = diagnoticsRes.get('Identifier');
        diagnosticsProgressRequest.CorrelationId = diagnoticsRes.get('CorrelationId');
        diagnosticsProgressRequest.Locale = diagnoticsRes.get('Locale');
        diagnosticsProgressRequest.TenantId = diagnoticsRes.get('TenantId');
        diagnosticsProgressRequest.SessionId = diagnoticsRes.get('SessionId');
        // we set AbandonCurrentRun to false right before we make api call
        dispatch(abandonDiagnosticsRunAction(false));
        const r = await GetDiagnosticsResultHandler(diagnosticsProgressRequest);
        // if the run has been abandoned AbandonCurrentRun will now be == true
        if (!!r && continueDiagnosticRun(getState)) {
          const response = createImmutableMap(r);
          let clearInterval = true;
          switch (response.get('Status')) {
            case DiagnosticsExecutionStatus.Executing:
              clearInterval = false;
              break;
            case DiagnosticsExecutionStatus.Failed:
              clearInterval = true;
              dispatch(setDiagnosticsStatusAction(DiagnosticsExecutionStatus.Failed));
              executeLogHandler(
                LogEventName.onGetDiagnosticsResultFailed,
                LogLevel.Info,
                'GetDiagnosticsResult failed.',
                uId,
                dialogId,
                logHandler,
                getState
              );
              break;
            case DiagnosticsExecutionStatus.Completed:
              clearInterval = true;
              executeLogHandler(
                LogEventName.onGetDiagnosticsResultSuceeded,
                LogLevel.Info,
                'GetDiagnosticsResult succeeded.',
                uId,
                dialogId,
                logHandler,
                getState
              );
              break;
            case DiagnosticsExecutionStatus.ContinueWithNext:
              clearInterval = true;
              executeLogHandler(
                LogEventName.onGetDiagnosticsResultSuceededWithNext,
                LogLevel.Info,
                'GetDiagnosticsResult succeeded with next.',
                uId,
                dialogId,
                logHandler,
                getState
              );
              break;
          }
          dispatch(setDiagnosticsRunProgress(response));
          if (clearInterval && timerId) {
            window.clearInterval(timerId);
            dispatch(stopDiagnosticsTimerAction());
          }
        }
      } else {
        window.clearInterval(timerId);
        dispatch(stopDiagnosticsTimerAction());
        dispatch(setDiagnosticsStatusAction(DiagnosticsExecutionStatus.Failed, true));
        executeLogHandler(
          LogEventName.onGetDiagnosticsResultTimeout,
          LogLevel.Info,
          'GetDiagnosticsResult timeout.',
          uId,
          dialogId,
          logHandler,
          getState
        );
      }
    } catch (ex) {
      if (continueDiagnosticRun(getState)) {
        window.clearInterval(timerId);
        dispatch(stopDiagnosticsTimerAction());
        dispatch(setDiagnosticsStatusAction(DiagnosticsExecutionStatus.Failed));
        executeLogHandler(
          LogEventName.onGetDiagnosticsResultFailed,
          LogLevel.Error,
          'GetDiagnosticsResult failed.',
          uId,
          dialogId,
          logHandler,
          getState
        );
      }
    }
    dispatch(safDiagnosticsCompletedAction());
  };
};

export const dispatchResetErrorAction = (componentKey: string) => {
  return async (dispatch: MDispatch) => {
    dispatch(resetErrorAction(componentKey));
  };
};

export const dispatchSetErrorAction = (componentKey: string, errorCode: ErrorCodes) => {
  return async (dispatch: MDispatch) => {
    if (errorCode) {
      dispatch(setErrorAction(componentKey, errorCode));
    }
  };
};

export const diapatchInputTextUpdateAction = (text: string, uId: string) => {
  return async (dispatch: MDispatch) => {
    dispatch(updateInputTextAction(text, uId));
  };
};

const handleFetchRelatedDialogError = async (
  retriesCount: number,
  maxRetriesCount: number,
  dispatch: MDispatch,
  getState: () => YodaState,
  logHandler: LogHandlerType,
  node: IImmutableMap<GraphNode>,
  contentRequestHandler: ContentRequestHandlerType,
  parentJsonPayload: IImmutableMap<JsonDocument>,
  sessionId: string,
  isUserSignedIn?: IsUserSignedInHandlerType,
  e?: Error,
  customErrorMessage?: string
) => {
  if (retriesCount < maxRetriesCount) {
    retriesCount += 1;
    dispatch(updateApiRetryCountAction(ExternalApi.ContentRequestHandler, retriesCount));
    await apiRetryBackoff(2 ** retriesCount * 100);
    dispatch(dispatchFetchRelatedDialog(node, contentRequestHandler, logHandler, isUserSignedIn));
    return false;
  } else {
    // construct error message
    let errorMessage = `Related dialog fetch failed ${customErrorMessage}`;
    if (e) {
      const eMessage = e.message;
      const eStack = e.stack;
      errorMessage = `Related dialog fetch failed. - errorMessage: ${eMessage}, errorStack: ${eStack}, retryCount: ${retriesCount}, maxRetries: ${maxRetriesCount}`;
    }
    executeLogHandler(
      LogEventName.onRelatedDialogFetchFailed,
      LogLevel.Info,
      errorMessage,
      node.get('uId'),
      node.get('dialogId'),
      logHandler,
      getState
    );
    dispatch(resetApiRetryCountAction(ExternalApi.ContentRequestHandler));
    if (node.get('failedTargetNodeId')) {
      const state = getState();
      const jsonPayload = getJsonPayload(jsonPayloadsSelector(state), node.get('dialogId'));
      const failedTargetNode = jsonPayload
        .get('nodes')
        .find(
          (n: IImmutableMap<GraphNode>) =>
            n.get('id') && n.get('id').toLowerCase() === node.get('failedTargetNodeId').toLowerCase()
        );
      dispatch(
        fetchDialogCompleteAction(
          jsonPayload,
          parentJsonPayload,
          sessionId,
          logHandler,
          isUserSignedIn,
          true,
          failedTargetNode
        )
      );
    }
  }
  return false;
};

export const handleRelatedDialogResult = async (
  response: string,
  dispatch: MDispatch,
  getState: () => YodaState,
  contentRequestHandler: ContentRequestHandlerType,
  logHandler: LogHandlerType,
  node: IImmutableMap<GraphNode>,
  retriesCount: number,
  maxRetriesCount: number,
  jsonPayload: IImmutableMap<JsonDocument>,
  parentJsonPayload: IImmutableMap<JsonDocument>,
  sessionId: string,
  isUserSignedIn?: IsUserSignedInHandlerType
) => {
  if (response) {
    const alchemyResources = JSON.parse(response);
    const solutionContent = JSON.parse(alchemyResources.SolutionContent);
    jsonPayload = deserializeToImmutableMap(JsonDocument, solutionContent);
    dispatch(fetchDialogCompleteAction(jsonPayload, parentJsonPayload, sessionId, logHandler, isUserSignedIn, false));
    executeLogHandler(
      LogEventName.onRelatedDialogFetchSuccess,
      LogLevel.Info,
      `Related dialog fetched successfully - ${jsonPayload.get('meta').get('id')}`,
      node.get('uId'),
      node.get('dialogId'),
      logHandler,
      getState
    );
  } else {
    const errorMessage = 'Fetching related dialog returned null';
    await handleFetchRelatedDialogError(
      retriesCount,
      maxRetriesCount,
      dispatch,
      getState,
      logHandler,
      node,
      contentRequestHandler,
      parentJsonPayload,
      sessionId,
      isUserSignedIn,
      null,
      errorMessage
    );
  }
};

export const dispatchFetchRelatedDialog = (
  node: IImmutableMap<GraphNode>,
  contentRequestHandler: ContentRequestHandlerType,
  logHandler?: LogHandlerType,
  isUserSignedIn?: IsUserSignedInHandlerType
) => {
  return async (dispatch: MDispatch, getState: () => YodaState) => {
    dispatch(fetchDialogStartedAction());
    const dialogId = node.get('dialogId');
    const targetDialogId = node.get('targetDialogId');
    const retry = getApiRetry(ExternalApi.ContentRequestHandler, getState);
    let retriesCount = retry.get('retriesCount');
    const maxRetriesCount = retry.get('maxRetriesCount');
    retriesCount = retriesCount || 1;
    if (!!targetDialogId) {
      executeLogHandler(
        LogEventName.onRedirectNodeRendered,
        LogLevel.Info,
        `Redirect Node rendered - ${targetDialogId}`,
        node.get('uId'),
        node.get('dialogId'),
        logHandler,
        getState
      );
      const state = getState();
      //search for jsonDocument with matched meta id in redux state of list of jsonDocuments
      let jsonPayload = getJsonPayload(jsonPayloadsSelector(state), targetDialogId);
      let parentJsonPayload = getJsonPayload(jsonPayloadsSelector(state), dialogId);
      const sessionId = sessionIdSelector(state);
      if (isNullOrUndefined(jsonPayload)) {
        //if next dialog of jsonDocument is not found, request content from alchemy resources api and find related jsondocument
        //after fetching related dialog from resources & new foundJsonDocument is assigned, update/push into current list of jsonDocuments in redux state
        contentRequestHandler(ContentType.Branching, targetDialogId, dialogId)
          .then(async (response: string) => {
            await handleRelatedDialogResult(
              response,
              dispatch,
              getState,
              contentRequestHandler,
              logHandler,
              node,
              retriesCount,
              maxRetriesCount,
              jsonPayload,
              parentJsonPayload,
              sessionId,
              isUserSignedIn
            );
          })
          .catch(async (error: Error) => {
            await handleFetchRelatedDialogError(
              retriesCount,
              maxRetriesCount,
              dispatch,
              getState,
              logHandler,
              node,
              contentRequestHandler,
              parentJsonPayload,
              sessionId,
              isUserSignedIn,
              error
            );
          });
      } else {
        //If the next dialog of jsonDocument is found already in redux state list, retrieve the data
        dispatch(
          fetchDialogCompleteAction(jsonPayload, parentJsonPayload, sessionId, logHandler, isUserSignedIn, true)
        );
      }
    } else {
      executeLogHandler(
        LogEventName.onRedirectNodeTargetDialogIdMissing,
        LogLevel.Info,
        `targetDialogId missing`,
        node.get('uId'),
        node.get('dialogId'),
        logHandler,
        getState
      );
    }
  };
};

export const dispatchUpdateInputFormFieldChanges = (node: IImmutableMap<GraphNode>) => {
  return async (dispatch: MDispatch, getState: () => YodaState) => {
    if(node){
      let inputFormField = node.get('InputFormField');
      inputFormField = inputFormField.set('IsMultilineText', node.get('isMultiLine'));
      const localizedFields = ['placeholder', 'tooltip'];
      localizedFields.forEach((field: string) => {
        let textField = node.get(field as keyof GraphNode) as IImmutableMap<IntentGraphTextField>;
        let localizedContent = textField ? textField.get('content') : '';
        const state = getState();
        let resource = getResource(resourcesSelector(state), globalVarsSelector(state), textField);
        if(!!resource){
          localizedContent = resource.SolutionContent;
        }
        switch (field) {
          case 'placeholder':
            inputFormField = inputFormField.set('LocalizedPlaceholder', localizedContent);
            break;
          case 'tooltip':
            inputFormField = inputFormField.set('LocalizedToolTip', localizedContent);
            break;
          default:
            break;
        }
      });
      dispatch(updateInputFormFieldsAction(inputFormField, node.get('uId')));
    }
  };
};

export const dispatchNodeResourcesFetch = (
  contentRequestHandler: ContentRequestHandlerType,
  logHandler: LogHandlerType,
  contentResources: ContentIdResource[],
  node: IImmutableMap<GraphNode>
) => {
  return async (dispatch: MDispatch, getState: () => YodaState) => {
    let errorMessage = '';
    let dialogId = '';
    let uId = '';
    if (!!node) {
      dialogId = node.get('dialogId');
      uId = node.get('uId');
      try {
        if (contentResources && contentResources.length > 0) {
          const state = getState();
          const resources = resourcesSelector(state);
          const resourcePromisesList: Promise<string>[] = [];
          contentResources.forEach((contentResource: ContentIdResource) => {
            if (contentResource && contentResource.contentId && contentResource.contentType) {
              const contentId = contentResource.contentId;
              const resource = resources.find((r: IImmutableMap<AlchemyResource>) => r.get('contentId') === contentId)
              if(!resource){
                const contentType = contentResource.contentType;
                const isDynamic = contentResource.isDynamic;
                dispatch(fetchAlchemyResourceAction(contentId, isDynamic, contentType));
                const resourcePromise = contentRequestHandler(contentType, contentId, dialogId);
                resourcePromisesList.push(resourcePromise);
              }
            } else {
              errorMessage += `- null contentId: ${contentResource.contentId}, contentType: ${contentResource.contentType}`;
            }
          });
          if (resourcePromisesList && resourcePromisesList.length > 0) {
            Promise.allSettled(resourcePromisesList).then((values: PromiseSettledResult<string>[]) => {
              values.forEach((value: PromiseSettledResult<string>, index: number) => {
                if (!value) {
                  errorMessage += `- empty promise for ${contentResources[index].contentId}`;
                  return;
                }
                if (value.status === 'fulfilled') {
                  // update resource
                  const targetContentId = contentResources[index].contentId;
                  const solutionContent = JSON.parse(value.value);
                  dispatch(setAlchemyResourceAction(solutionContent, targetContentId));
                } else if (value.status === 'rejected') {
                  errorMessage += `- resource fetch failed for contentId: ${contentResources[index].contentId} for this reason: ${value.reason}`;
                }
              });
            });
          }
        }
      } catch (error) {
        errorMessage += `- caught exception ${error.message}`;
      }
    }
    if (!!errorMessage) {
      executeLogHandler(
        LogEventName.onContentRetrievalError,
        LogLevel.Error,
        errorMessage,
        uId,
        dialogId,
        logHandler,
        getState
      );
    }
  };
};
